import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {PlanFilterSchema} from '../../../api/DBSchema/Plan';


function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function PlanFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );
        
    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);
    

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');

        setCreationFromDate(null);
        setCreationToDate(null);
        
        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);


    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Free text search
        if (freeTextSearch && freeTextSearch.length) {
            filterRequestObj[PlanFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }
        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[PlanFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[PlanFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate
    ]);


    return (
        <FilterLayout clearAllAction={resetToDefaults}>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Modification tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(PlanFilterPane);